import { useEffect, useRef, useState } from "react";
import { Hand, Loader2, Minus, Plus, RefreshCw } from "lucide-react";
import { NOTES, fromTaps, withBpm } from "@/lib/tempoKey";

const DEFAULT = { bpm: 100, offset: 0, tonic: 0, mode: "major", source: "manual", confidence: 0 };
const field = "rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-[13px] text-white focus:border-sky-300/50 focus:outline-none";
const sq = "grid h-8 min-w-8 place-items-center rounded-lg border border-white/10 bg-white/[0.04] px-2 font-mono text-[11px] text-white/80 transition-colors duration-200 hover:bg-white/[0.1]";
const label = "font-mono text-[10px] uppercase tracking-[0.16em] text-white/45";
const shown = (bpm) => String(Math.round(bpm * 10) / 10);

/** Correct the tempo (type it, nudge, ½× / 2×, or tap along) and the key; every change is saved straight away. */
export default function TempoKeyEditor({ tempo, onChange, onRedetect, getSongTime, detecting }) {
  const t = tempo || DEFAULT;
  const [draft, setDraft] = useState(shown(t.bpm));
  const [taps, setTaps] = useState(0);
  const tap = useRef({ list: [], song: false });
  useEffect(() => setDraft(shown(t.bpm)), [t.bpm]);
  const setBpm = (v) => onChange(withBpm(t, v));
  const commit = () => { const v = Number(draft); if (Number.isFinite(v) && v >= 40 && v <= 240) setBpm(v); else setDraft(shown(t.bpm)); };
  const onTap = () => {
    const song = getSongTime?.() ?? null;
    const now = song ?? performance.now() / 1000;
    const tp = tap.current;
    const prev = tp.list[tp.list.length - 1];
    if (prev != null && (now - prev > 2 || now <= prev || tp.song !== (song != null))) tp.list = [];
    tp.song = song != null;
    tp.list.push(now);
    setTaps(tp.list.length);
    const r = fromTaps(tp.list, tp.song);
    if (r) onChange(r.offset != null ? { ...t, ...r, source: "manual" } : withBpm(t, r.bpm));
  };
  return (
    <div data-testid="tempo-editor" className="space-y-4">
      <div>
        <p className={label}>Tempo (beats per minute)</p>
        <div className="mt-1.5 flex items-center gap-1.5">
          <button type="button" data-testid="tempo-bpm-down" onClick={() => setBpm(t.bpm - 1)} className={sq} aria-label="Slower"><Minus className="h-3.5 w-3.5" /></button>
          <input data-testid="tempo-bpm-input" inputMode="decimal" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && commit()} className={`${field} w-20 text-center font-mono`} aria-label="Tempo in BPM" />
          <button type="button" data-testid="tempo-bpm-up" onClick={() => setBpm(t.bpm + 1)} className={sq} aria-label="Faster"><Plus className="h-3.5 w-3.5" /></button>
          <button type="button" data-testid="tempo-half" onClick={() => setBpm(t.bpm / 2)} className={sq} title="Half as fast">Half</button>
          <button type="button" data-testid="tempo-double" onClick={() => setBpm(t.bpm * 2)} className={sq} title="Twice as fast">Double</button>
        </div>
        <button type="button" data-testid="tempo-tap" onClick={onTap}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-sky-300/40 bg-sky-500/15 py-2.5 font-display text-[13px] font-semibold text-sky-50 transition-transform duration-150 active:scale-[0.97]">
          <Hand className="h-4 w-4" /> Tap along {taps ? <span data-testid="tempo-tap-count" className="font-mono text-[10px] text-sky-100/70">({taps} taps)</span> : null}
        </button>
        <p className="mt-1.5 text-[11px] leading-snug text-white/50">Tap on every beat, 4 taps or more. Tap while the music plays and the clicks line up with your taps too.</p>
      </div>
      <div>
        <p className={label}>Key</p>
        <div className="mt-1.5 flex gap-2">
          <select data-testid="tempo-key-tonic" value={t.tonic ?? 0} onChange={(e) => onChange({ ...t, tonic: Number(e.target.value), source: "manual" })} className={`${field} flex-1`}>
            {NOTES.map((n, i) => <option key={n} value={i}>{n}</option>)}
          </select>
          <select data-testid="tempo-key-mode" value={t.mode} onChange={(e) => onChange({ ...t, mode: e.target.value, source: "manual" })} className={`${field} flex-1`}>
            <option value="major">major</option>
            <option value="minor">minor</option>
          </select>
        </div>
      </div>
      {onRedetect ? (
        <button type="button" data-testid="tempo-redetect" onClick={onRedetect} disabled={detecting} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-white/55 hover:text-white disabled:opacity-40">
          {detecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Detect again from the music
        </button>
      ) : null}
    </div>
  );
}
